import { forwardRef, useId } from "react";
import type { InputHTMLAttributes } from "react";

/**
 * Beyond Sure — Input primitive.
 *
 * Visual:
 *   • Paper surface, Ink-16% bottom-border (line-only field; reads editorial
 *     rather than form-y)
 *   • On focus, the bottom-border swaps to BRAND GREEN — not blue — so the
 *     screen reads as on-brand even in micro-interactions.
 *   • On error, the border swaps to solid Ink at 2px; helper text turns to
 *     Ink at full strength.
 *   • Label sits above the field, small caps, tight tracking
 *   • Helper / error slot has a fixed minimum height so the layout never
 *     jumps when validation kicks in
 *
 * A11y:
 *   • <label htmlFor=…> wired automatically via useId
 *   • aria-invalid + aria-describedby when error is present
 *   • role=alert + aria-live=polite on the helper slot when errored
 */
interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  error?: string | null;
  helper?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, helper, className = "", ...rest },
  ref,
) {
  const id = useId();
  const helperId = `${id}-helper`;
  const errored = Boolean(error);

  return (
    <div className={["flex flex-col", className].join(" ")}>
      <label
        htmlFor={id}
        className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-ink-60)]"
      >
        {label}
      </label>

      <input
        ref={ref}
        id={id}
        aria-invalid={errored || undefined}
        aria-describedby={error || helper ? helperId : undefined}
        className={[
          "mt-2 w-full bg-transparent",
          "font-sans text-[16px] text-[var(--color-ink)]",
          "placeholder:text-[var(--color-ink-40)]",
          "border-0 border-b py-3",
          "outline-none transition-[border-color,box-shadow]",
          "duration-[var(--duration-base)] ease-[var(--ease-standard)]",
          errored
            ? "border-b-2 border-[var(--color-ink)]"
            : "border-b-[1.5px] border-[var(--color-ink-16)] focus:border-[var(--color-brand)] focus:border-b-2",
        ].join(" ")}
        {...rest}
      />

      <div
        id={helperId}
        role={errored ? "alert" : undefined}
        aria-live="polite"
        className={[
          "min-h-[18px] mt-2 text-[12.5px] leading-[1.35]",
          errored ? "text-[var(--color-ink)] font-medium" : "text-[var(--color-ink-60)]",
        ].join(" ")}
      >
        {error || helper || " "}
      </div>
    </div>
  );
});

export default Input;
